import moment from 'moment';
import { emptyGuid } from '@/shared/utils/guid';
/** Neutral default filter state (period defaults to the current month). */
export const createDefaultFilterState = () => (Object.assign(Object.assign({ relation: null, resource: null, training: null, userId: null, ownCompanies: [], legalEntities: [], taskTemplates: [], finalInvoiceStreamId: null, crmTypes: [], withLabels: [], withoutLabels: [], withEmployeeLabels: [], withoutEmployeeLabels: [], invoiceDateFrom: null, invoiceDateUntil: null, assignmentTemplateId: null, endFiscalYears: [], referenceYear: null, invoiceId: null, intraCompanyInvoiceId: null, relationGroups: [], relationHandlingLevels: [], relationTypes: [], isLocked: null, includeInactive: null, period: 'month' }, computePeriodRange('month')), { showAdvanceFilters: false }));
/**
 * Computes the `{ from, to }` date range for a named period. Ported verbatim from the
 * controller's `setPeriod`. `all`/unknown clears the range (open-ended).
 */
export function computePeriodRange(period) {
    const today = moment.utc().startOf('day');
    switch (period) {
        case 'today':
            return { from: today.toDate(), to: today.toDate() };
        case 'yesterday': {
            const d = today.clone().add(-1, 'days');
            return { from: d.toDate(), to: d.toDate() };
        }
        case 'week': {
            const from = today.clone().startOf('isoWeek');
            return {
                from: from.toDate(),
                to: from
                    .clone()
                    .add(6, 'days')
                    .toDate(),
            };
        }
        case 'month':
            return {
                from: today
                    .clone()
                    .date(1)
                    .toDate(),
                to: moment
                    .utc()
                    .endOf('month')
                    .toDate(),
            };
        case 'quarter':
            return {
                from: moment
                    .utc()
                    .startOf('quarter')
                    .toDate(),
                to: moment
                    .utc()
                    .endOf('quarter')
                    .toDate(),
            };
        case 'year':
            return {
                from: moment
                    .utc()
                    .startOf('year')
                    .toDate(),
                to: moment
                    .utc()
                    .endOf('year')
                    .toDate(),
            };
        case 'prevWeek': {
            const from = today
                .clone()
                .startOf('isoWeek')
                .week(today.week() - 1);
            return {
                from: from.toDate(),
                to: from
                    .clone()
                    .add(6, 'days')
                    .toDate(),
            };
        }
        case 'prevMonth': {
            const from = today
                .clone()
                .date(1)
                .add(-1, 'months');
            return {
                from: from.toDate(),
                to: moment
                    .utc(from)
                    .clone()
                    .endOf('month')
                    .toDate(),
            };
        }
        case 'prevQuarter':
            return {
                from: moment
                    .utc()
                    .add(-3, 'months')
                    .startOf('quarter')
                    .toDate(),
                to: moment
                    .utc()
                    .add(-3, 'months')
                    .endOf('quarter')
                    .toDate(),
            };
        case 'prevYear':
            return {
                from: moment
                    .utc()
                    .add(-1, 'year')
                    .startOf('year')
                    .toDate(),
                to: moment
                    .utc()
                    .add(-1, 'year')
                    .endOf('year')
                    .toDate(),
            };
        case 'all':
        default:
            return { from: null, to: null };
    }
}
const formatDate = (date) => date ? moment.utc(date).format('YYYY-MM-DD') : null;
/**
 * Builds the server-facing filter object (the React equivalent of `getFilters`).
 * `relationId` is suppressed when a final invoice stream is selected, and all dates are
 * formatted as `YYYY-MM-DD`.
 */
export function buildServerFilters(state) {
    return {
        relationId: state.relation && !state.finalInvoiceStreamId ? state.relation.id : null,
        resourceId: state.resource ? state.resource.id : null,
        ownCompanies: state.ownCompanies || [],
        legalEntities: state.legalEntities || [],
        taskTemplates: state.taskTemplates || [],
        finalInvoiceStreamId: state.finalInvoiceStreamId,
        crmTypes: state.crmTypes || [],
        withLabels: state.withLabels,
        withoutLabels: state.withoutLabels,
        withEmployeeLabels: state.withEmployeeLabels,
        withoutEmployeeLabels: state.withoutEmployeeLabels,
        invoiceDateFrom: formatDate(state.invoiceDateFrom),
        invoiceDateUntil: formatDate(state.invoiceDateUntil),
        userId: state.userId || emptyGuid,
        from: formatDate(state.from),
        to: formatDate(state.to),
        assignmentTemplateId: state.assignmentTemplateId,
        endFiscalYears: state.endFiscalYears,
        referenceYear: state.referenceYear,
        invoiceId: state.invoiceId,
        intraCompanyInvoiceId: state.intraCompanyInvoiceId,
        relationGroups: state.relationGroups,
        relationHandlingLevels: state.relationHandlingLevels,
        relationTypes: state.relationTypes,
        trainingId: state.training ? state.training.id : null,
        isLocked: state.isLocked,
        includeInactive: state.includeInactive,
    };
}
/**
 * Whether any filter deviates from its default (React equivalent of `hasFilters`,
 * excluding the table's own column filters which the container checks separately).
 *
 * Compares the built server filters against the defaults generically, so newly added
 * filters are covered automatically without extending this function. `showAdvanceFilters`
 * is a UI toggle (not part of the server filters) and is therefore ignored; the user
 * filter is neutralised when the user can't view others, since it's then fixed to
 * themselves and never user-applied.
 */
export function hasActiveFilters(state, canViewOthers) {
    const normalize = (s) => {
        const filters = buildServerFilters(s);
        if (!canViewOthers)
            filters.userId = emptyGuid;
        return filters;
    };
    return JSON.stringify(normalize(state)) !== JSON.stringify(normalize(createDefaultFilterState()));
}
